import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Package, Truck, Users, DollarSign, Activity, TrendingUp, Wifi, WifiOff, MapPin, Navigation, Route, Eye, Calendar, Search, Maximize2, Clock } from 'lucide-react';
import MapView from '@/components/MapView';
import { useFreightSocket } from '@/hooks/useFreightSocket';
import { useNavigate } from 'react-router-dom';
import { getLoadStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function AdminDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailLoad, setDetailLoad] = useState(null);
  const [driverDetail, setDriverDetail] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [kpiRange, setKpiRange] = useState('all');
  const [kpiCustomStart, setKpiCustomStart] = useState('');
  const [kpiCustomEnd, setKpiCustomEnd] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [tableStatusFilter, setTableStatusFilter] = useState('all');
  const [historyLoads, setHistoryLoads] = useState(null);
  const [mapShowDrivers, setMapShowDrivers] = useState(true);
  const [mapShowRoutes, setMapShowRoutes] = useState(true);
  const [mapShowRequested, setMapShowRequested] = useState(false);
  const [activeShifts, setActiveShifts] = useState([]);
  const { lastEvent, connected } = useFreightSocket(token, user);

  const fetchData = (t) => {
    const authToken = t || token;
    if (!authToken) { setLoading(false); return; }
    setError(null);
    api.get('/analytics/dashboard')
      .then(r => setStats(r.data))
      .catch(err => {
        console.error('Dashboard fetch error:', err);
        setError('Failed to load dashboard data. Check that the backend is running.');
      })
      .finally(() => setLoading(false));
    api.get('/admin/active-shifts').then(r => setActiveShifts(r.data || [])).catch(() => {});
  };

  useEffect(() => { if (token) fetchData(token); }, [token]);
  useEffect(() => {
    if (lastEvent && ['load_update', 'location_update'].includes(lastEvent.type)) fetchData();
  }, [lastEvent]);

  const fetchHistory = () => {
    const params = {};
    if (dateRange.start) params.start = new Date(dateRange.start).toISOString();
    if (dateRange.end) params.end = new Date(dateRange.end + 'T23:59:59').toISOString();
    api.get('/analytics/full', { params })
      .then(r => setHistoryLoads(r.data)).catch(console.error);
  };

  const setQuickRange = (type) => {
    const now = new Date();
    let s, e = now.toISOString().slice(0, 10);
    if (type === 'mtd') s = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    else if (type === 'last_month') {
      const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      s = pm.toISOString().slice(0, 10);
      e = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
    } else if (type === 'last_7') s = new Date(now - 7 * 864e5).toISOString().slice(0, 10);
    setDateRange({ start: s, end: e });
  };

  useEffect(() => {
    if (dateRange.start || dateRange.end) fetchHistory();
  }, [dateRange.start, dateRange.end]);

  if (loading) return <DashboardSkeleton />;
  if (error) return (
    <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
      <p className="text-red-400 text-sm">{error}</p>
      <Button variant="outline" onClick={() => { setLoading(true); fetchData(); }}
        className="border-input text-muted-foreground hover:bg-muted">Retry</Button>
    </div>
  );

  const drivers = stats?.active_drivers || [];
  const activeDrivers = drivers.filter(d => d.active_loads?.length > 0);
  const recentLoads = stats?.recent_loads || [];
  const activeLoads = recentLoads.filter(ld => ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(ld.status));
  const requestedLoads = recentLoads.filter(ld => ld.status === 'posted');

  // Build map markers with enriched data for popups
  let mapMarkers = [];
  let mapLoads = [];
  if (mapShowDrivers) {
    mapMarkers.push(...drivers.filter(d => d.last_location?.lat).map(d => ({
      id: d.user_id, lat: d.last_location.lat, lng: d.last_location.lng,
      label: d.name, detail: `${d.active_loads?.length || 0} active loads`, type: 'truck', color: '#E5A100',
      detailId: d.user_id, detailType: 'driver',
      driverInfo: {
        phone: d.phone, truck_type: d.truck_type,
        currentLoad: d.active_loads?.[0]?.cargo_description || 'None',
        status: d.active_loads?.length > 0 ? 'On Road' : 'Idle',
      },
    })));
  }
  if (mapShowRoutes) {
    mapMarkers.push(...activeLoads.flatMap(ld => {
      const drv = drivers.find(d => d.user_id === ld.driver_id);
      const info = { cargo: ld.cargo_description, weight: ld.weight_lbs, rate: ld.rate, status: ld.status?.replace(/_/g, ' '), driver: drv?.name, customer: ld.shipper?.name };
      return [
        { id: `${ld.load_id}-p`, lat: ld.pickup_lat, lng: ld.pickup_lng, label: ld.pickup_name || ld.pickup_address, type: 'pickup', detailId: ld.load_id, detailType: 'load', loadInfo: info },
        { id: `${ld.load_id}-d`, lat: ld.delivery_lat, lng: ld.delivery_lng, label: ld.delivery_name || ld.delivery_address, type: 'delivery', detailId: ld.load_id, detailType: 'load', loadInfo: info },
      ];
    }));
    mapLoads.push(...activeLoads.map(ld => {
      const drv = drivers.find(d => d.user_id === ld.driver_id);
      return { ...ld, driver_lat: drv?.last_location?.lat, driver_lng: drv?.last_location?.lng };
    }));
  }
  if (mapShowRequested) {
    mapMarkers.push(...requestedLoads.flatMap(ld => {
      const info = { cargo: ld.cargo_description, weight: ld.weight_lbs, rate: ld.rate, status: 'Posted' };
      return [
        { id: `${ld.load_id}-p`, lat: ld.pickup_lat, lng: ld.pickup_lng, label: `REQ: ${ld.pickup_name || ld.pickup_address}`, type: 'pickup', detailId: ld.load_id, detailType: 'load', loadInfo: info },
        { id: `${ld.load_id}-d`, lat: ld.delivery_lat, lng: ld.delivery_lng, label: `REQ: ${ld.delivery_name || ld.delivery_address}`, type: 'delivery', detailId: ld.load_id, detailType: 'load', loadInfo: info },
      ];
    }));
    mapLoads.push(...requestedLoads);
  }

  const handleMapViewDetails = (type, id) => {
    if (type === 'driver') {
      const drv = drivers.find(d => d.user_id === id);
      if (drv) setDriverDetail(drv);
    } else {
      const ld = recentLoads.find(l => l.load_id === id);
      if (ld) setDetailLoad(ld);
    }
  };

  // Filter recent loads for table
  let filteredLoads = recentLoads;
  if (tableStatusFilter !== 'all') filteredLoads = filteredLoads.filter(l => l.status === tableStatusFilter);
  if (tableSearch) {
    const q = tableSearch.toLowerCase();
    filteredLoads = filteredLoads.filter(l =>
      l.cargo_description?.toLowerCase().includes(q) || l.pickup_address?.toLowerCase().includes(q) ||
      l.delivery_address?.toLowerCase().includes(q) || l.shipper?.name?.toLowerCase().includes(q)
    );
  }

  // KPI filtering by date range
  const getKpiFilteredStats = () => {
    if (kpiRange === 'all') return stats;
    const now = new Date();
    let cutoff, endDate;
    if (kpiRange === '7d') cutoff = new Date(now - 7 * 864e5);
    else if (kpiRange === 'mtd') cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (kpiRange === 'last_month') cutoff = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    else if (kpiRange === 'custom' && kpiCustomStart) {
      cutoff = new Date(kpiCustomStart);
      endDate = kpiCustomEnd ? new Date(kpiCustomEnd + 'T23:59:59') : now;
    }
    if (!cutoff) return stats;
    const filtered = recentLoads.filter(l => {
      const d = new Date(l.created_at);
      return d >= cutoff && (!endDate || d <= endDate);
    });
    const completed = filtered.filter(l => ['completed', 'invoiced'].includes(l.status));
    const active = filtered.filter(l => ['assigned', 'at_pickup', 'in_transit', 'at_delivery'].includes(l.status));
    const rev = completed.reduce((s, l) => s + (l.rate || 0), 0);
    const miles = completed.reduce((s, l) => s + (l.distance_miles || 0), 0);
    return { ...stats, total_loads: filtered.length, active_loads: active.length, total_revenue: rev, total_miles: miles };
  };
  const kpiStats = getKpiFilteredStats();

  const statCards = [
    { icon: Package, label: 'Total Loads', value: kpiStats?.total_loads || 0, accent: 'text-brand-accent' },
    { icon: Activity, label: 'Active', value: kpiStats?.active_loads || 0, accent: 'text-foreground' },
    { icon: Truck, label: 'Drivers', value: kpiStats?.total_drivers || 0, accent: 'text-emerald-400' },
    { icon: Users, label: 'Clients', value: kpiStats?.total_clients || 0, accent: 'text-purple-400' },
    { icon: DollarSign, label: 'Revenue', value: `$${(kpiStats?.total_revenue || 0).toLocaleString()}`, accent: 'text-brand-accent' },
    { icon: TrendingUp, label: 'Avg $/km', value: `$${kpiStats?.total_miles ? ((kpiStats.total_revenue || 0) / (kpiStats.total_miles * 1.60934)).toFixed(2) : '0'}`, accent: 'text-foreground' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
        </div>
        <div className="flex items-center gap-2">
          {[{ id: 'all', label: 'ALL' }, { id: '7d', label: '7D' }, { id: 'mtd', label: 'MTD' }, { id: 'last_month', label: 'LAST MO' }].map(r => (
            <button key={r.id} onClick={() => setKpiRange(r.id)}
              className={`text-[10px] tracking-wider font-bold px-3 py-1.5 rounded-md transition-colors ${
                kpiRange === r.id ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground border border-border hover:text-muted-foreground'
              }`}>{r.label}</button>
          ))}
          <input type="date" value={kpiCustomStart} onChange={e => { setKpiCustomStart(e.target.value); setKpiRange('custom'); }}
            className="bg-muted border border-input text-muted-foreground text-xs rounded-md px-2 py-1.5 h-7 w-32" />
          <input type="date" value={kpiCustomEnd} onChange={e => { setKpiCustomEnd(e.target.value); setKpiRange('custom'); }}
            className="bg-muted border border-input text-muted-foreground text-xs rounded-md px-2 py-1.5 h-7 w-32" />
          <Badge className={`text-xs ${connected ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-muted/50 text-muted-foreground border-border'}`} data-testid="admin-ws-status">
            {connected ? <><Wifi className="w-3 h-3 mr-1" /> LIVE</> : <><WifiOff className="w-3 h-3 mr-1" /> OFFLINE</>}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {statCards.map((s, i) => (
          <Card key={i} className="bg-card border-border backdrop-blur-sm hover:border-input transition-colors">
            <CardContent className="p-4">
              <s.icon className={`w-4 h-4 ${s.accent} mb-2`} strokeWidth={1.5} />
              <p className="text-xl font-bold text-foreground">{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Drivers On Duty */}
      <Card className="bg-card border-border mb-6" data-testid="drivers-on-duty-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" /> Drivers On Duty ({activeShifts.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {activeShifts.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">No drivers currently on duty</p>
          ) : (
            <div className="space-y-2">
              {activeShifts.map((entry, idx) => {
                const elapsed = Math.round((Date.now() - new Date(entry.clock_in_at).getTime()) / 60000);
                const statusColors = {
                  driving: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                  on_duty: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                  break: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                  off_duty: 'bg-muted text-muted-foreground border-border',
                };
                const badgeClass = statusColors[entry.duty_status] || statusColors.off_duty;
                return (
                  <div key={entry.user_id || idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-xl border border-border">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-brand-accent/20 flex items-center justify-center text-xs font-bold text-brand-accent">
                        {entry.driver_name?.charAt(0) || '?'}
                      </div>
                      <span className="text-sm font-medium text-foreground">{entry.driver_name || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] ${badgeClass}`}>
                        {(entry.duty_status || 'on_duty').replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {elapsed >= 60 ? `${Math.floor(elapsed / 60)}h ${elapsed % 60}m` : `${elapsed}m`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fleet Map + Active Drivers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2 bg-card border-border" data-testid="fleet-map-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-muted-foreground font-medium">Fleet Map</CardTitle>
              <div className="flex items-center gap-2">
                <ToggleBtn label="Drivers" active={mapShowDrivers} onClick={() => setMapShowDrivers(!mapShowDrivers)} testId="toggle-drivers" />
                <ToggleBtn label="Active Routes" active={mapShowRoutes} onClick={() => setMapShowRoutes(!mapShowRoutes)} testId="toggle-routes" />
                <ToggleBtn label="Requested" active={mapShowRequested} onClick={() => setMapShowRequested(!mapShowRequested)} testId="toggle-requested" />
                <button onClick={() => window.open('/admin/fleet-map', '_blank')}
                  className="p-1.5 rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-input transition-colors"
                  title="Open full screen map">
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 px-4 pb-4">
            <div className="h-[380px] rounded-xl overflow-hidden border border-border">
              <MapView markers={mapMarkers} loads={mapLoads} dark={false} zoom={4}
                showLegend showSatelliteToggle onViewDetails={handleMapViewDetails} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border" data-testid="active-drivers-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground font-medium">Active Drivers ({activeDrivers.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2 max-h-[380px] overflow-y-auto">
            {activeDrivers.length === 0 && <p className="text-muted-foreground text-sm py-4 text-center">No active drivers</p>}
            {activeDrivers.map(drv => (
              <div key={drv.user_id}
                className="p-3 bg-muted/50 rounded-xl border border-border hover:border-brand-accent/30 cursor-pointer transition-colors"
                onClick={() => setDriverDetail(drv)} data-testid={`active-driver-${drv.user_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-brand-accent/20 flex items-center justify-center text-xs font-bold text-brand-accent">{drv.name?.charAt(0)}</div>
                    <span className="text-sm font-medium text-foreground">{drv.name}</span>
                  </div>
                  <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{drv.active_loads?.length} LOAD{drv.active_loads?.length !== 1 ? 'S' : ''}</Badge>
                </div>
                {/* Route info for active loads */}
                {drv.active_loads?.slice(0, 2).map(ld => (
                  <div key={ld.load_id} className="ml-9 mt-1.5 text-[10px]">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                      <span className="truncate max-w-[80px]">{(ld.pickup_name || ld.pickup_address || '').split(',')[0]}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
                      <span className="truncate max-w-[80px]">{(ld.delivery_name || ld.delivery_address || '').split(',')[0]}</span>
                    </div>
                    <div className="flex gap-2 text-muted-foreground mt-0.5">
                      <Badge className="text-[8px] h-4 bg-muted text-muted-foreground border-0">{ld.status?.replace(/_/g, ' ')}</Badge>
                      <span>{Math.round((ld.distance_miles || 0) * 1.60934)} km</span>
                      <span>${ld.rate}</span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground ml-9 mt-1">
                  <span>{drv.total_km?.toLocaleString()} km total</span>
                  <span>${drv.total_earnings?.toLocaleString()} earned</span>
                </div>
              </div>
            ))}
            {drivers.filter(d => !d.active_loads?.length).length > 0 && (
              <>
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold pt-2">IDLE DRIVERS</p>
                {drivers.filter(d => !d.active_loads?.length).map(drv => (
                  <div key={drv.user_id} className="p-2 bg-muted/30 rounded-lg border border-border/50 cursor-pointer hover:border-input transition-colors"
                    onClick={() => setDriverDetail(drv)} data-testid={`idle-driver-${drv.user_id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">{drv.name?.charAt(0)}</div>
                      <span className="text-xs text-muted-foreground">{drv.name}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{drv.completed_loads_count} loads done</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Loads with Status Column */}
      <Card className="bg-card border-border mb-6" data-testid="recent-loads-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-muted-foreground font-medium">Recent Loads</CardTitle>
            <div className="flex items-center gap-3">
              <select value={tableStatusFilter} onChange={e => setTableStatusFilter(e.target.value)}
                className="bg-muted border border-input text-foreground text-[10px] rounded-md px-2 py-1 appearance-none cursor-pointer">
                <option value="all">All Status</option>
                {stats?.status_breakdown && Object.entries(stats.status_breakdown).map(([status, count]) => (
                  <option key={status} value={status}>{status.replace('_', ' ').toUpperCase()} ({count})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input value={tableSearch} onChange={e => setTableSearch(e.target.value)} placeholder="Search loads..."
                className="w-full bg-muted border border-input text-foreground text-xs rounded-lg pl-8 pr-3 py-1.5" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">CARGO</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">SHIPPER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">PICKUP</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DROP-OFF</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">WEIGHT</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">KM</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">RATE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLoads.map(ld => (
                <TableRow key={ld.load_id} className="border-border hover:bg-muted/30 cursor-pointer"
                  onClick={() => setDetailLoad(ld)} data-testid={`recent-load-${ld.load_id}`}>
                  <TableCell className="text-xs text-foreground font-medium max-w-[120px] truncate">{ld.cargo_description}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{ld.shipper?.name || '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">{ld.pickup_name || ld.pickup_address?.split(',')[0]}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">{ld.delivery_name || ld.delivery_address?.split(',')[0]}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{ld.weight_lbs ? `${(ld.weight_lbs / 1000).toFixed(1)}k` : '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{milesToKm(ld.distance_miles)}</TableCell>
                  <TableCell className="text-xs text-brand-accent font-mono">${ld.rate?.toLocaleString()}</TableCell>
                  <TableCell><Badge className={`text-[10px] rounded-sm border ${getLoadStatusClasses(ld.status)}`}>{ld.status?.replace('_', ' ').toUpperCase()}</Badge></TableCell>
                  <TableCell><Eye className="w-3.5 h-3.5 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* History with Date Range */}
      <Card className="bg-card border-border" data-testid="history-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-muted-foreground font-medium flex items-center gap-2"><Calendar className="w-4 h-4" /> Load History</CardTitle>
            <div className="flex items-center gap-2">
              {['last_7', 'mtd', 'last_month'].map(t => (
                <Button key={t} variant="ghost" size="sm" onClick={() => setQuickRange(t)}
                  className="text-[10px] text-muted-foreground hover:text-foreground h-7 px-2" data-testid={`range-${t.replace('_', '-')}`}>
                  {t === 'last_7' ? '7D' : t === 'mtd' ? 'MTD' : 'LAST MO'}
                </Button>
              ))}
              <Input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
                className="bg-muted border-input text-muted-foreground h-7 text-xs w-32" data-testid="history-start" />
              <Input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
                className="bg-muted border-input text-muted-foreground h-7 text-xs w-32" data-testid="history-end" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {historyLoads ? (
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-foreground">{historyLoads.total_loads}</p><p className="text-[10px] text-muted-foreground">LOADS</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-brand-accent">${historyLoads.total_revenue?.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">REVENUE</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-foreground">{historyLoads.total_km?.toLocaleString()} km</p><p className="text-[10px] text-muted-foreground">DISTANCE</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-emerald-400">${historyLoads.avg_rate_per_km}/km</p><p className="text-[10px] text-muted-foreground">AVG RATE</p>
              </div>
            </div>
          ) : <p className="text-muted-foreground text-sm text-center py-4">Select a date range to view history</p>}
        </CardContent>
      </Card>

      {/* Load Detail Dialog */}
      <Dialog open={!!detailLoad} onOpenChange={() => setDetailLoad(null)}>
        <DialogContent className="bg-surface border-border text-foreground max-w-2xl">
          <DialogHeader><DialogTitle>Load Details</DialogTitle></DialogHeader>
          {detailLoad && <LoadDetailView load={detailLoad} drivers={drivers} token={token} onLoadClick={setDetailLoad}
            onNavigate={(path, state) => { setDetailLoad(null); navigate(path, { state }); }} />}
        </DialogContent>
      </Dialog>

      {/* Driver Detail Dialog */}
      <Dialog open={!!driverDetail} onOpenChange={() => setDriverDetail(null)}>
        <DialogContent className="bg-surface border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Driver Profile</DialogTitle></DialogHeader>
          {driverDetail && <DriverDetailView driver={driverDetail} drivers={drivers} token={token}
            onLoadClick={(ld) => { setDriverDetail(null); setDetailLoad(ld); }}
            onNavigate={(path) => { setDriverDetail(null); navigate(path); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToggleBtn({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick}
      className={`text-[10px] tracking-wider font-bold px-2.5 py-1 rounded-md transition-colors ${
        active ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground border border-border hover:text-muted-foreground'
      }`} data-testid={testId}>{label}</button>
  );
}

function LoadDetailView({ load, drivers, token, onLoadClick, onNavigate }) {
  const drv = drivers.find(d => d.user_id === load.driver_id);
  const markers = [
    { id: 'p', lat: load.pickup_lat, lng: load.pickup_lng, label: load.pickup_name || load.pickup_address, type: 'pickup' },
    { id: 'd', lat: load.delivery_lat, lng: load.delivery_lng, label: load.delivery_name || load.delivery_address, type: 'delivery' },
  ];
  if (drv?.last_location?.lat) {
    markers.push({ id: 'truck', lat: drv.last_location.lat, lng: drv.last_location.lng, label: drv.name, type: 'truck', color: '#E5A100' });
  }
  const loadWithDriver = { ...load, driver_lat: drv?.last_location?.lat, driver_lng: drv?.last_location?.lng };

  // Check if driver has other loads on route
  const otherLoads = drv?.active_loads?.filter(al => al.load_id !== load.load_id) || [];

  return (
    <div className="space-y-4" data-testid="load-detail-view">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">{load.cargo_description}</h3>
        <Badge className={`text-xs border ${getLoadStatusClasses(load.status)}`}>{load.status?.replace('_', ' ').toUpperCase()}</Badge>
      </div>
      <div className="h-[250px] rounded-xl overflow-hidden border border-border">
        <MapView markers={markers} loads={[loadWithDriver]} dark={false} zoom={6} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-muted/50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-[10px] text-muted-foreground tracking-wider">PICKUP</span></div>
          {load.pickup_name && <p className="text-sm font-bold text-foreground">{load.pickup_name}</p>}
          <p className="text-xs text-muted-foreground">{load.pickup_address}</p>
        </div>
        <div className="bg-muted/50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="text-[10px] text-muted-foreground tracking-wider">DROP-OFF</span></div>
          {load.delivery_name && <p className="text-sm font-bold text-foreground">{load.delivery_name}</p>}
          <p className="text-xs text-muted-foreground">{load.delivery_address}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-muted/50 rounded-lg p-3"><p className="text-lg font-bold text-brand-accent">${load.rate?.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">RATE</p></div>
        <div className="bg-muted/50 rounded-lg p-3"><p className="text-lg font-bold text-foreground">{milesToKm(load.distance_miles)} km</p><p className="text-[10px] text-muted-foreground">DISTANCE</p></div>
        <div className="bg-muted/50 rounded-lg p-3"><p className="text-lg font-bold text-foreground">{load.weight_lbs?.toLocaleString()} lbs</p><p className="text-[10px] text-muted-foreground">WEIGHT</p></div>
      </div>
      {load.shipper && (
        <div className="bg-muted/50 rounded-xl p-3">
          <p className="text-[10px] text-muted-foreground tracking-wider mb-2">SHIPPER</p>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-sm font-bold text-purple-400">{load.shipper.name?.charAt(0)}</div>
            <div>
              <p className="text-sm font-medium text-brand-accent hover:underline cursor-pointer" onClick={() => onNavigate?.('/admin/customers', { customerId: load.client_id })}>{load.shipper.name}</p>
              <p className="text-xs text-muted-foreground">{load.shipper.email}{load.shipper.phone ? ` | ${load.shipper.phone}` : ''}</p>
            </div>
          </div>
        </div>
      )}
      {drv && (
        <div className="bg-muted/50 rounded-xl p-3">
          <p className="text-[10px] text-muted-foreground tracking-wider mb-2">ASSIGNED DRIVER</p>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center text-sm font-bold text-brand-accent">{drv.name?.charAt(0)}</div>
            <div className="flex-1">
              <p className="text-sm font-medium text-brand-accent hover:underline cursor-pointer" onClick={() => onNavigate?.('/admin/drivers', { driverId: drv.user_id })}>{drv.name}</p>
              <p className="text-xs text-muted-foreground">{drv.email}</p>
            </div>
            {drv.last_location?.lat && <p className="text-[10px] text-cyan-400"><MapPin className="w-3 h-3 inline mr-1" />{drv.last_location.lat.toFixed(3)}, {drv.last_location.lng.toFixed(3)}</p>}
          </div>
          {otherLoads.length > 0 && (
            <div className="mt-2 pt-2 border-t border-input">
              <p className="text-[10px] text-amber-400 font-bold mb-1">OTHER LOADS ON THIS ROUTE ({otherLoads.length})</p>
              {otherLoads.map(ol => (
                <div key={ol.load_id} className="flex items-center justify-between p-2 bg-muted/80 rounded-lg mb-1 cursor-pointer hover:bg-muted/50"
                  onClick={() => onLoadClick(ol)} data-testid={`other-load-${ol.load_id}`}>
                  <span className="text-xs text-foreground">{ol.cargo_description}</span>
                  <Badge className={`text-[10px] ${getLoadStatusClasses(ol.status)}`}>{ol.status?.replace('_', ' ').toUpperCase()}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DriverDetailView({ driver, drivers, token, onLoadClick, onNavigate }) {
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const hasActiveLoad = driver.active_loads?.length > 0;
  const firstLoad = driver.active_loads?.[0];

  const mapMarkers = [];
  const mapLoads = [];
  if (driver.last_location?.lat) {
    mapMarkers.push({ id: 'drv', lat: driver.last_location.lat, lng: driver.last_location.lng, label: driver.name, type: 'truck', color: '#E5A100' });
  }
  if (hasActiveLoad) {
    driver.active_loads.forEach(ld => {
      mapMarkers.push(
        { id: `${ld.load_id}-p`, lat: ld.pickup_lat, lng: ld.pickup_lng, label: ld.pickup_name || 'Pickup', type: 'pickup' },
        { id: `${ld.load_id}-d`, lat: ld.delivery_lat, lng: ld.delivery_lng, label: ld.delivery_name || 'Delivery', type: 'delivery' },
      );
      mapLoads.push({ ...ld, driver_lat: driver.last_location?.lat, driver_lng: driver.last_location?.lng });
    });
  }

  const sendMessage = async (loadId) => {
    if (!msgInput.trim() || !loadId) return;
    setSending(true);
    try {
      await api.post('/messages', { load_id: loadId, content: msgInput.trim() });
      setMsgInput('');
      import('sonner').then(m => m.toast.success('Message sent'));
    } catch { import('sonner').then(m => m.toast.error('Failed')); }
    setSending(false);
  };

  const tabs = [
    { id: 'profile', label: 'PROFILE' },
    { id: 'loads', label: 'LOADS' },
    { id: 'message', label: 'MESSAGE' },
  ];

  return (
    <div className="space-y-4" data-testid="driver-detail-view">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-brand-accent/20 flex items-center justify-center text-2xl font-bold text-brand-accent">{driver.name?.charAt(0)}</div>
        <div>
          <h3 className="text-lg font-bold">{driver.name}</h3>
          <p className="text-sm text-muted-foreground">{driver.email}</p>
          {driver.phone && <p className="text-xs text-muted-foreground">{driver.phone}</p>}
        </div>
      </div>

      {/* Map of driver location / route — use regular map, not satellite */}
      {mapMarkers.length > 0 && (
        <div className="h-[200px] rounded-xl overflow-hidden border border-border">
          <MapView markers={mapMarkers} loads={mapLoads} zoom={6} />
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-muted/50 rounded-lg p-2"><p className="text-sm font-bold text-brand-accent">{driver.total_km?.toLocaleString()} km</p><p className="text-[10px] text-muted-foreground">TOTAL KM</p></div>
        <div className="bg-muted/50 rounded-lg p-2"><p className="text-sm font-bold text-emerald-400">${driver.total_earnings?.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">EARNED</p></div>
        <div className="bg-muted/50 rounded-lg p-2"><p className="text-sm font-bold text-foreground">{driver.completed_loads_count}</p><p className="text-[10px] text-muted-foreground">LOADS DONE</p></div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`text-[10px] tracking-wider font-bold px-3 py-1.5 rounded-md transition-colors flex-1 ${
              activeTab === t.id ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground border border-border'
            }`} data-testid={`driver-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'profile' && (
        <div className="space-y-3">
          {driver.license_number && (
            <div className="bg-muted/50 rounded-lg p-3"><p className="text-[10px] text-muted-foreground mb-1">CDL License</p><p className="text-sm text-foreground font-mono">{driver.license_number}</p></div>
          )}
          {driver.truck_type && (
            <div className="bg-muted/50 rounded-lg p-3"><p className="text-[10px] text-muted-foreground mb-1">Truck Type</p><p className="text-sm text-foreground">{driver.truck_type}</p></div>
          )}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-[10px] text-muted-foreground tracking-wider mb-2">EMERGENCY CONTACT</p>
            {driver.emergency_contact_name ? (
              <div>
                <p className="text-sm text-foreground">{driver.emergency_contact_name}</p>
                {driver.emergency_contact_phone && <p className="text-xs text-muted-foreground">{driver.emergency_contact_phone}</p>}
                {driver.emergency_contact_relation && <p className="text-xs text-muted-foreground">{driver.emergency_contact_relation}</p>}
              </div>
            ) : <p className="text-xs text-muted-foreground">Not set</p>}
          </div>
        </div>
      )}

      {activeTab === 'loads' && (
        <div className="space-y-2">
          {(!driver.active_loads || driver.active_loads.length === 0) && <p className="text-muted-foreground text-sm text-center py-4">No active loads</p>}
          {(driver.active_loads || []).map(ld => (
            <div key={ld.load_id} className="bg-muted/50 rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onLoadClick(ld)} data-testid={`driver-load-${ld.load_id}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-foreground font-medium">{ld.cargo_description}</p>
                <Badge className={`text-[10px] ${getLoadStatusClasses(ld.status)}`}>{ld.status?.replace('_', ' ').toUpperCase()}</Badge>
              </div>
              <p className="text-[10px] text-muted-foreground">{ld.pickup_name || ld.pickup_address?.split(',')[0]} &rarr; {ld.delivery_name || ld.delivery_address?.split(',')[0]}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{milesToKm(ld.distance_miles)} km | ${ld.rate?.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'message' && (
        <div className="space-y-2">
          {firstLoad ? (
            <div>
              <p className="text-xs text-muted-foreground mb-2">Send message about: <span className="text-foreground">{firstLoad.cargo_description}</span></p>
              <div className="flex gap-2">
                <input value={msgInput} onChange={e => setMsgInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage(firstLoad.load_id)}
                  placeholder="Type a message..." className="flex-1 bg-muted border border-input text-foreground rounded-lg px-3 py-2 text-sm" data-testid="driver-msg-input" />
                <Button onClick={() => sendMessage(firstLoad.load_id)} disabled={sending || !msgInput.trim()}
                  className="bg-brand-accent text-brand-accent-foreground font-bold rounded-lg px-4" data-testid="driver-msg-send">Send</Button>
              </div>
            </div>
          ) : <p className="text-muted-foreground text-sm text-center py-4">No active load to message about</p>}
        </div>
      )}

      {onNavigate && (
        <Button onClick={() => onNavigate(`/admin/drivers/${driver.user_id}`)}
          variant="outline" className="w-full border-input text-muted-foreground hover:bg-muted mt-2">
          View Full Driver Profile
        </Button>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 bg-muted rounded animate-pulse" />
      <div className="grid grid-cols-6 gap-3">{Array(6).fill(0).map((_, i) => <div key={i} className="h-24 bg-muted rounded animate-pulse" />)}</div>
      <div className="grid grid-cols-3 gap-4"><div className="col-span-2 h-[380px] bg-muted rounded animate-pulse" /><div className="h-[380px] bg-muted rounded animate-pulse" /></div>
    </div>
  );
}
